import { useCallback } from 'react';

import {
  DefaultMenuItem,
  GetContextMenuItems,
  GetContextMenuItemsParams,
  GetMainMenuItems,
} from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { Delete, Duplicate } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import useDuplicateFlowMutation from '../api/mutations/useDuplicateFlowMutation.js';
import { useSetDeleteFlowModalContext } from '../atoms/deleteFlowModal.js';
import { ApprovalContextMenuItemParams } from '../features/ApproversTable/components/MenuItem';
import MenuItem from '../features/ApproversTable/components/MenuItem.jsx';
import type { ApprovalTableItemData } from '../types/approversTable.js';

const getSortingItemList = (
  sort?: 'asc' | 'desc' | null,
): DefaultMenuItem[] => {
  if (sort === 'asc') {
    return ['sortDescending', 'sortUnSort'];
  }

  if (sort === 'desc') {
    return ['sortAscending', 'sortUnSort'];
  }

  return ['sortAscending', 'sortDescending'];
};

const useGridContextMenu = () => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const setDeleteFlowModalContext = useSetDeleteFlowModalContext();

  const queryClient = useQueryClient();
  const { mutateAsync: duplicateFlow } = useDuplicateFlowMutation();

  const handleDuplicate = useCallback(
    async (item: ApprovalTableItemData) => {
      try {
        await duplicateFlow({
          urlParams: {
            flowId: item.id,
          },
        });
        ApprovalFlows.listFlows.invalidateAll(queryClient, {});
        enqueueSnackbar({
          variant: 'success',
          title: t('Approval flow was duplicated successfully.'),
          removable: true,
        });
      } catch {
        enqueueSnackbar({
          variant: 'error',
          title: t('Approval flow was not duplicated.'),
          removable: true,
        });
      }
    },
    [duplicateFlow, t, queryClient],
  );

  const handleDelete = useCallback(
    (item: ApprovalTableItemData) => {
      setDeleteFlowModalContext({
        isOpened: true,
        flowId: item.id,
        inUse: item.inUse,
      });
    },
    [setDeleteFlowModalContext],
  );

  const getContextMenuItems = useCallback<
    GetContextMenuItems<ApprovalTableItemData>
  >(
    (params: GetContextMenuItemsParams<ApprovalTableItemData>) => {
      const data = params.node?.data;

      if (!data) {
        return [];
      }

      return [
        {
          name: t('Duplicate'),
          menuItem: MenuItem,
          menuItemParams: {
            IconComponent: Duplicate,
            a11yLabel: 'Duplicate the flow',
          } satisfies ApprovalContextMenuItemParams,
          disabled: isReadOnly,
          action: () => handleDuplicate(data),
        },
        'separator',
        {
          name: t('Delete'),
          menuItem: MenuItem,
          disabled: isReadOnly,
          menuItemParams: {
            IconComponent: Delete,
            color: 'error',
            a11yLabel: 'Delete the flow',
          } satisfies ApprovalContextMenuItemParams,
          action: () => handleDelete(data),
        },
      ];
    },
    [t, handleDuplicate, handleDelete, isReadOnly],
  );

  const getMainMenuItems = useCallback<GetMainMenuItems<ApprovalTableItemData>>(
    (params) => {
      if (!params.column) {
        return [];
      }

      const sort = params.column.getSort();
      const colDef = params.column.getColDef();

      const sortingItemList = colDef.sortable ? getSortingItemList(sort) : [];
      const separatorAfterSortingItemList: DefaultMenuItem[] =
        sortingItemList.length ? ['separator'] : [];

      return [
        ...sortingItemList,
        ...separatorAfterSortingItemList,
        'pinSubMenu',
        'separator',
        'autoSizeThis',
        'autoSizeAll',
        'separator',
        'columnChooser',
      ];
    },
    [],
  );

  return {
    getContextMenuItems,
    getMainMenuItems,
  };
};

export default useGridContextMenu;
